import type { InvoiceStatus, KitchenWorkflow } from "@/lib/api/types";
import { printStartsFood } from "@/modules/kitchen/lib/workflow";

/**
 * WHICH button is the till's primary action right now, and — separately —
 * whether the bill may be closed.
 *
 * The order panel has always known this implicitly — it renders Send, Close,
 * Settle or Print from the invoice's status and the basket — and at narrow
 * widths the summary bar has to know exactly the same thing from outside the
 * panel. Two independent readings of "what does this screen want next" is how a
 * bar ends up offering Close over a basket that has not been fired, so the rule
 * is one pure function per question and both surfaces read them.
 *
 * It answers a QUESTION, not a permission: `enabled` folds in the grants and
 * the D22 close gate, but a busy command is the caller's own state and stays
 * there (`isBusy` on one screen must disable both surfaces at once).
 *
 * ## THE PRIMARY SLOT HOLDS ONE MEANING FOR THE LIFE OF A STATUS (UX-round D14, X2)
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * This function used to return `fire` or `close` for the SAME `status ===
 * "OPEN"`, switching on `draftCount` — and `draftCount` hits 0 the instant a
 * fire succeeds. Same rectangle, same `variant="primary"`, same fill, and the
 * two Arabic labels («ابعت للمطبخ» → «اقفل الفاتورة») are near-homographs of
 * the pane toggle sitting a few millimetres away. So the act of firing a round
 * put a bill-ending control under a thumb that was aimed at something else,
 * and `closeInvoice` ran with no dialog behind it.
 *
 * The rule now, from the interaction expert's X2: **a label may change because
 * the PERSON acted; it may never change because the DATA moved.** For the life
 * of `OPEN` the primary is Fire — disabled, not replaced, once the basket is
 * empty (there is nothing to send; `pos.closeBlockedHint` and its queued twin
 * are the panel's own sentences for why Close is the other control). Close is
 * no longer a primary anywhere: it is a distinct secondary control, and
 * `tillCloseAction` below is the ONE place its gate lives.
 */
export type TillActionKind = "fire" | "requestCashier" | "close" | "settle" | "newOrder";

/** What may stand in the primary slot. Close deliberately cannot (UX-round D14/D15). */
export type TillPrimaryKind = Exclude<TillActionKind, "close">;

export type TillPrimary = {
  kind: TillPrimaryKind;
  /** False when the grant is missing or the command would be refused. */
  enabled: boolean;
  /**
   * Whether this session may do the act AT ALL (founder, 2026-10-06: "if the
   * action is not doable, remove it"). A missing grant HIDES the control; a
   * passing state (an empty basket) only disables it, because a tap from now
   * it will work.
   */
  permitted: boolean;
};

/**
 * What D22 refuses to close over, by kind (T6 / D173): an unanswered diner
 * request, an unsent basket, a plate the kitchen was told about and never
 * started.
 */
export type CloseBlocking = { pending: number; drafts: number; queued: number };

/**
 * WHY a greyed Close is greyed (T6 / D173) — the one reason the till names,
 * with its count. The first that holds wins, in the order a person clears
 * them: answer the table, send the basket, start the food.
 */
export type CloseBlockReason = { kind: "pending" | "drafts" | "queued"; count: number };

/** The close control's state, or `null` where closing is not on the table. */
export type TillClose = {
  /** False when the grant is missing or D22 refuses the close. */
  enabled: boolean;
  /** Why it is disabled, or null when nothing D22 counts holds it. */
  reason: CloseBlockReason | null;
};

export function closeBlockReason(blocking: CloseBlocking): CloseBlockReason | null {
  if (blocking.pending > 0) return { kind: "pending", count: blocking.pending };
  if (blocking.drafts > 0) return { kind: "drafts", count: blocking.drafts };
  if (blocking.queued > 0) return { kind: "queued", count: blocking.queued };
  return null;
}

export const NO_CLOSE_BLOCKING: CloseBlocking = { pending: 0, drafts: 0, queued: 0 };

export function tillPrimaryAction(input: {
  status: InvoiceStatus;
  /** Plates still in the basket — with none there is nothing to send. */
  draftCount: number;
  canFire: boolean;
  canSettle: boolean;
  /**
   * D120 — may build the order (`INVOICE_APPEND`). Without the kitchen rung,
   * that person's primary is "Send to cashier" instead of "Send to kitchen".
   */
  canRequestCashier?: boolean;
}): TillPrimary {
  if (input.status === "OPEN" && !input.canFire && input.canRequestCashier) {
    return {
      kind: "requestCashier",
      enabled: input.draftCount > 0,
      permitted: true,
    };
  }
  if (input.status === "OPEN") {
    // FIRE FOR THE LIFE OF `OPEN`. An empty basket disables it; it never hands
    // the slot to another act, because the person did not ask for one.
    return {
      kind: "fire",
      enabled: input.canFire && input.draftCount > 0,
      permitted: input.canFire,
    };
  }
  if (input.status === "CLOSED") {
    return { kind: "settle", enabled: input.canSettle, permitted: input.canSettle };
  }
  // SETTLED / CANCELLED: the bill is finished and the panel is showing its
  // overlay. What is left to do is start the next one.
  return { kind: "newOrder", enabled: true, permitted: true };
}

/**
 * MAY THIS BILL BE CLOSED — the D22 gate, in the one place both surfaces read.
 *
 * It moved here with the control (spec keeper E1: *"the D22 gate is not
 * negotiable and must move with the control: wherever Close lands it stays
 * `canClose && blockingCount === 0`"*). Nothing about the gate changed; only
 * the slot the control sits in did.
 *
 * `null` — not a disabled control — on a bill that is not OPEN: there is no
 * close to offer on a closed, settled or cancelled bill, and a permanently
 * dead button is not a state, it is clutter. CLOSED's own undo is `pos.reopen`.
 */
export function tillCloseAction(input: {
  status: InvoiceStatus;
  /** Everything D22 refuses to close over, by kind. */
  blocking: CloseBlocking;
  canClose: boolean;
}): TillClose | null {
  if (input.status !== "OPEN") return null;
  // Not offered at all without the grant (founder, 2026-10-06): a waiter takes
  // the order and the cashier closes it, so a greyed Close on the waiter's
  // till read as something they could do. D22 still disables it for those who
  // may close.
  if (!input.canClose) return null;
  const reason = closeBlockReason(input.blocking);
  return { enabled: reason === null, reason };
}

/**
 * THE ONE TAP BESIDE A GREYED CLOSE (T6, the cashier's amendment; D173).
 *
 * D22 stays a block: a close over QUEUED would move plates to SERVED without
 * passing PREPARING, the only step that deducts stock. So when the only thing
 * holding the close is unstarted food, the till offers the act that clears it:
 *
 *  - `startAndClose` «بدء التحضير والإغلاق» — where no paper starts the food
 *    (the screen workflows, and «بدون شاشة أو طابعة» for a plate fired before a
 *    switch): `startPreparingAtTill` for every QUEUED plate (the server flags
 *    it as a till start and the speed report leaves it out), then the ordinary
 *    close with its covers prompt, hand-over and undo. Needs `ITEM_PREPARE` and
 *    `INVOICE_CLOSE`.
 *  - `resendTicket` «إعادة إرسال التذكرة» — in a PAPER workflow an unstarted
 *    plate means the paper never printed, so the till never starts it
 *    silently: it resends the ticket to the station printer
 *    (`reprintTicket NOT_RECEIVED`; no printer → the browser chit). Needs
 *    `KITCHEN_PRINT`.
 *
 * Nothing for a pending request or an unsent basket: their acts (answer, send)
 * are already on the screen.
 */
export type CloseUnblockKind = "startAndClose" | "resendTicket";

export function closeUnblockAction(input: {
  reason: CloseBlockReason | null;
  workflow: KitchenWorkflow;
  canPrepare: boolean;
  canClose: boolean;
  canPrint: boolean;
}): CloseUnblockKind | null {
  if (input.reason?.kind !== "queued") return null;
  if (printStartsFood(input.workflow)) return input.canPrint ? "resendTicket" : null;
  return input.canPrepare && input.canClose ? "startAndClose" : null;
}

/**
 * The `busyKey` the invoice screen dispatches this action under, so the bar and
 * the panel spin the same button rather than each guessing a string.
 * `newOrder` is a navigation, not a command, so it has no key.
 *
 * It maps every till ACT, not only the ones that may be primary: `close` left
 * the primary slot (UX-round D15) but it is still dispatched under `"close"` on both
 * surfaces, and E1 is explicit that this map is what keeps them spinning the
 * same button.
 */
export function tillPrimaryBusyKey(kind: TillActionKind): string | null {
  switch (kind) {
    case "fire":
      return "fire";
    case "close":
      return "close";
    case "settle":
      return "settle";
    case "requestCashier":
      return "request-cashier";
    case "newOrder":
      return null;
  }
}

/**
 * The tickets «إعادة إرسال التذكرة» resends: each ticket holding a QUEUED
 * plate, once, in the order the plates are listed.
 */
export function queuedTicketIds(
  plates: readonly { fulfillmentState: string; kitchenTicketId: string | null }[],
): string[] {
  return [
    ...new Set(
      plates
        .filter((plate) => plate.fulfillmentState === "QUEUED")
        .map((plate) => plate.kitchenTicketId)
        .filter((ticketId): ticketId is string => ticketId !== null),
    ),
  ];
}
